import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import Axios from '../axios-config/axiosInstance.ts';
import { 
  IClientReq, 
  IClientResponse, 
  ICommonAPIResponse, 
  ICommonListPayloadDto, 
  IClientListResponse, 
  IClientAPIResponse, 
  IAssignmentResponse, 
  IAssignmentListResponse,
  IAssignmentRes,
  IAssignmentReq,
  IVendorRes,
  IVendorResponse
} from '../type/type.ts';

interface AssignmentState {
  assignments: IAssignmentRes[];
  loading: boolean;
  error: string | null;
  assignmentSelect: {
    label: string;
    value: number;
  }[];
  offset: number;
  limit: number;
  sortField: string;
  sortOrder: number;
  searchInput: string;
  dataCount: number;
  getIUserByIdValue: any;
  availableVendors: IVendorRes[];
}

const initialState: AssignmentState = {
  assignments: [],
  assignmentSelect: [],
  loading: false,
  error: null,
  offset: 0,
  limit: 10,
  sortField: 'createdOn',
  sortOrder: 1,
  searchInput: '',
  dataCount: 0,
  getIUserByIdValue: [],
  availableVendors: []
};

export const getAllAssignments = createAsyncThunk<
  IAssignmentListResponse,
  ICommonListPayloadDto,
  { rejectValue: string }
>(
  'assignment/getAllAssignments',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await Axios.get<IAssignmentResponse>('assignments/customers', {
        params: payload,
      });

      if (!response.data.success) {
        return rejectWithValue(response.data.message);
      }

      const res = response.data.data;

      return {
        assignments: res.data,
        total: res.total,
        offset: res.offset,
        limit: res.limit,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch assignments');
    }
  }
);



// export const fetchAssignment = createAsyncThunk<
//   IAssignmentResponse,
//   { assignmentId: number },
//   { rejectValue: string }
// >(
//   'assignment/fetchById',
//   async ({ assignmentId }, { rejectWithValue }) => {
//     try {
//       const response = await Axios.get<IAssignmentResponse>(`assignments/${assignmentId}`); 
//       return response.data;
//     } catch (error: any) {
//       return rejectWithValue(error.response?.data?.message || 'Failed to fetch assignment');
//     }
//   }
// );  

export const createAssignment = createAsyncThunk<
  IAssignmentResponse,
  IAssignmentReq,
  { rejectValue: string }
>(
  'assignment/createAssignment',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await Axios.post<ICommonAPIResponse>('assignments', payload);
      if (!response.data.success) {
        return rejectWithValue(response.data.message);
      }
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create assignment');
    }
  }
);

export const getVendorsByService = createAsyncThunk<
  IVendorRes[],
  number,
  { rejectValue: string }
>(
  'assignment/getVendorsByService',
  async (serviceId, { rejectWithValue }) => {
    try {
      const response = await Axios.get<ICommonAPIResponse>(`/assignments/vendors/${serviceId}`, {
        params: {
          service_id: serviceId,
          limit: 100 // Get all vendors for this service
        }
      });
      if (!response.data.success) {
        return rejectWithValue(response.data.message);
      }
      // The API returns data directly in the data field
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch vendors');
    }
  }
);

export const assignVendor = createAsyncThunk<
  ICommonAPIResponse,
  { assignmentId: number; vendorId: number },
  { rejectValue: string }
>(  
  'assignments/assignVendor',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await Axios.put<ICommonAPIResponse>(
        `/assignments/requests/${payload.assignmentId}`, 
        { vendor_id: payload.vendorId }
      );
      if (!response.data.success) {
        return rejectWithValue(response.data.message);
      }
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to assign vendor');
    }
  }
);


export const removeCustomerRequests = createAsyncThunk<
  ICommonAPIResponse,
  number,
  { rejectValue: string }
>(
  'assignment/removeCustomerRequests',
  async (requestId, { rejectWithValue }) => {
    try {
      const response = await Axios.delete<ICommonAPIResponse>(`/assignments/requests/${requestId}`);
      if (!response.data.success) {
        return rejectWithValue(response.data.message);
      }
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to remove customer request');
    }
  }
);

const assignmentSlice = createSlice({
  name: 'assignmentState',
  initialState,
  reducers: {
    setOffset: (state, action) => {
      state.offset = action.payload;
    },
    setLimit: (state, action) => {
      state.limit = action.payload;
    },
    setSortField: (state, action) => {
      state.sortField = action.payload;
    },
    setSortOrder: (state, action) => {
      state.sortOrder = action.payload;
    },
    setSearchInput: (state, action) => {
      state.searchInput = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllAssignments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllAssignments.fulfilled, (state, action) => {
        state.loading = false;
        state.assignments = action.payload.assignments;
        state.dataCount = action.payload.total;
        state.offset = action.payload.offset;
        state.limit = action.payload.limit;
        state.error = null;
      })
      
      .addCase(getAllAssignments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || 'Failed to load assignments';
      })
      .addCase(createAssignment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createAssignment.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(createAssignment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || 'Failed to create assignment';
      })
      .addCase(getVendorsByService.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getVendorsByService.fulfilled, (state, action) => {
        state.loading = false;
        state.availableVendors = action.payload;
        state.error = null;
      })
      .addCase(getVendorsByService.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || 'Failed to fetch vendors';
      })
      .addCase(removeCustomerRequests.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeCustomerRequests.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(removeCustomerRequests.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || 'Failed to remove customer request';
      });
  },
});

export const { setOffset, setLimit, setSortField, setSortOrder, setSearchInput } = assignmentSlice.actions;
export default assignmentSlice.reducer;